import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toast';

const CONNECTION_TIMEOUT_MS = 5000;
const POLLING_INTERVAL_MS = 10000;

const showToast = (type, message) => {
  toast[type](message + '\u200B'.repeat(Date.now() % 100));
};

export const useAgentConnection = (onBothConnected) => {
  const [isChecking, setIsChecking]         = useState(false);
  const [agentConnected, setAgentConnected] = useState(false);
  const [extensionConnected, setExtensionConnected] = useState(false);
  const [agentVersion, setAgentVersion]     = useState(null);
  const [extensionVersion, setExtensionVersion] = useState(null);

  const timeoutIdRef     = useRef(null);
  const intervalRef      = useRef(null);
  const silentRef        = useRef(false);
  const isManualCheckRef = useRef(false);
  const hasNavigatedRef  = useRef(false);

  const clearConnectionTimeout = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const handleCheckConnection = useCallback((silent = false) => {
    if (isManualCheckRef.current && silent) return;

    if (!silent) isManualCheckRef.current = true;
    silentRef.current = silent;

    clearConnectionTimeout();
    if (!silent) setIsChecking(true);
    setAgentConnected(false);
    setExtensionConnected(false);

    window.postMessage(
      { source: 'ROBITEST_CLOUD', action: 'CHECK_CONNECTIVITY' },
      window.location.origin
    );

    timeoutIdRef.current = setTimeout(() => {
      isManualCheckRef.current = false;
      setIsChecking(false);
      setAgentConnected(false);
      setExtensionConnected(false);
      if (!silent) {
        showToast('error', 'Connection timed out — ensure the agent is running');
      }
    }, CONNECTION_TIMEOUT_MS);
  }, [clearConnectionTimeout]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== window) return;
      const { source, type, agent_version, extension_version } = event.data;
      if (source !== 'ROBITEST_AGENT') return;

      clearConnectionTimeout();
      isManualCheckRef.current = false;
      setIsChecking(false);

      if (type === 'CONNECTIVITY_OK') {
        setAgentConnected(true);
        setExtensionConnected(true);
        if (agent_version) setAgentVersion(agent_version);
        if (extension_version) setExtensionVersion(extension_version);
        if (!silentRef.current) showToast('success', 'Agent and Extension connected!');
      } else if (type === 'CONNECTIVITY_FAILED') {
        setAgentConnected(false);
        setExtensionConnected(false);
        if (!silentRef.current) showToast('error', 'Failed to connect to Agent or Extension');
      }
    };

    window.addEventListener('message', handleMessage);
    handleCheckConnection();
    intervalRef.current = setInterval(() => handleCheckConnection(true), POLLING_INTERVAL_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearConnectionTimeout();
      clearInterval(intervalRef.current);
    };
  }, [handleCheckConnection, clearConnectionTimeout]);

  useEffect(() => {
    if (!agentConnected || !extensionConnected || hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    const timer = setTimeout(onBothConnected, 2500);
    return () => clearTimeout(timer);
  }, [agentConnected, extensionConnected, onBothConnected]);

  return {
    isChecking,
    agentConnected,
    extensionConnected,
    agentVersion,
    extensionVersion,
    handleCheckConnection,
  };
};